// ITAM-2583 — pure resolution of the branch delivery fee from the branch SERVICE
// items. Extracted from React so it is unit-testable without a network/render.
//
// Root cause it fixes: /marketplace/{m}/{b} returns deliveryPrice/deliveryPriceFast
// = null for many branches, so the FE could not price a delivery order. The mobile
// apps instead fetch the branch SERVICE items and read the fee off the item named
// (exactly) "Service fee standard" / "Service fee fast":
//   - iOS   : serviceItemId = itemResponse.id, deliveryPrice = itemPrice ?? itemLowestPrice
//   - Android: fetches the id but hardcodes 250/500 (we DON'T — data-driven, like iOS)
// Selection is by the EXACT backend name (not the mobile <300/>300 heuristic).

import {
  BranchServiceItem,
  CncOrderDeliveryType,
} from "../types/consultOrder.types";

export type ServiceFeeStatus = "ok" | "missing" | "duplicate" | "malformed";

export interface ResolvedServiceFee {
  status: ServiceFeeStatus;
  serviceItemId: number | null;
  /** Usable fee (a genuine numeric 0 is valid) only when status === "ok"; else null. */
  price: number | null;
}

/**
 * Exact backend SERVICE-item names the delivery fee is resolved by. Mirrors
 * CnCOrderServiceImpl (findByMarketplace_IdAndItem_Name with these literals).
 */
export const SERVICE_FEE_ITEM_NAME: Record<CncOrderDeliveryType, string> = {
  STANDARD_DELIVERY: "Service fee standard",
  FAST_DELIVERY: "Service fee fast",
};

const isUsableNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Resolve the delivery fee for a fulfillment from the branch SERVICE items.
 *   - missing   : no SERVICE item matches the exact fee name
 *   - duplicate : more than one matches (ambiguous — refuse to guess)
 *   - malformed : matched, but the id or price is unusable (null/NaN/negative)
 *   - ok        : a single match with a usable id + numeric price (0 allowed)
 * Only "ok" permits a delivery submission; every other status blocks it.
 */
export const resolveServiceFee = (
  items: BranchServiceItem[],
  fulfillment: CncOrderDeliveryType
): ResolvedServiceFee => {
  const targetName = SERVICE_FEE_ITEM_NAME[fulfillment];
  const matches = items.filter(
    (item) => (item.itemResponse?.name ?? "").trim() === targetName
  );

  if (matches.length === 0) {
    return { status: "missing", serviceItemId: null, price: null };
  }
  if (matches.length > 1) {
    return { status: "duplicate", serviceItemId: null, price: null };
  }

  const match = matches[0];
  const id = match.itemResponse?.id ?? null;
  // itemPrice takes precedence; ?? preserves a genuine 0 (only null/undefined fall through).
  const rawPrice = match.itemPrice ?? match.itemLowestPrice ?? null;
  const price = isUsableNumber(rawPrice) ? rawPrice : null;

  if (id == null || price == null || price < 0) {
    return { status: "malformed", serviceItemId: id, price: null };
  }
  return { status: "ok", serviceItemId: id, price };
};
